import {
  Client,
  CurrencyBalance,
  CurrencyType
} from '../models/trainer.models';

/**
 * Normalizes a single currency balance row from Member/PTSearch (camelCase or PascalCase).
 */
export function normalizeCurrencyBalanceRow(row: Record<string, unknown>): CurrencyBalance | null {
  const balance = Number(row['Balance'] ?? row['balance']);
  if (!Number.isFinite(balance)) {
    return null;
  }
  const type = Number(row['Type'] ?? row['type']);
  const scale = Number(row['Scale'] ?? row['scale']);
  const currencyId = Number(row['CurrencyId'] ?? row['currencyId'] ?? 0);
  const productUnit = String(row['ProductUnit'] ?? row['productUnit'] ?? '');
  return {
    CurrencyId: Number.isFinite(currencyId) ? currencyId : 0,
    CurrencyName: String(row['CurrencyName'] ?? row['currencyName'] ?? ''),
    Balance: balance,
    Scale: Number.isFinite(scale) ? scale : 0,
    ProductUnit: productUnit,
    Type: (Number.isFinite(type) ? type : 0) as CurrencyType
  };
}

/**
 * Maps Member/PTSearch `Records[]` into `Client` rows (same rules as Add Session roster).
 */
export function normalizePtSearchClientRecords(list: unknown): Client[] {
  if (!Array.isArray(list)) {
    return [];
  }
  return list.map((raw: Record<string, unknown>) => {
    const r = raw as Record<string, unknown>;
    const id = Number(r['Id'] ?? r['id'] ?? 0);
    const schedObj = r['Schedule'];
    const nestedSid =
      schedObj != null && typeof schedObj === 'object'
        ? Number((schedObj as Record<string, unknown>)['Id'] ?? (schedObj as Record<string, unknown>)['id'] ?? 0)
        : NaN;
    const scheduleId = Number(
      r['ScheduleId'] ??
        r['scheduleId'] ??
        r['MemberTrainingScheduleId'] ??
        r['memberTrainingScheduleId'] ??
        r['TrainingScheduleId'] ??
        r['trainingScheduleId'] ??
        (Number.isFinite(nestedSid) && nestedSid > 0 ? nestedSid : NaN)
    );
    const safeId = Number.isFinite(id) ? id : 0;
    const safeSid = Number.isFinite(scheduleId) ? scheduleId : 0;

    const balRaw = r['CurrencyBalances'] ?? r['currencyBalances'];
    let CurrencyBalances: CurrencyBalance[] | undefined;
    if (Array.isArray(balRaw) && balRaw.length) {
      const normalized = balRaw
        .map(entry =>
          entry != null && typeof entry === 'object'
            ? normalizeCurrencyBalanceRow(entry as Record<string, unknown>)
            : null
        )
        .filter((x): x is CurrencyBalance => x != null);
      if (normalized.length) {
        CurrencyBalances = normalized;
      }
    }

    const ptRaw = r['PTSessions'] ?? r['ptSessions'];
    const gsRaw = r['GroupSessions'] ?? r['groupSessions'];
    const next: Client = { ...raw, Id: safeId, ScheduleId: safeSid } as Client;
    if (CurrencyBalances?.length) {
      next.CurrencyBalances = CurrencyBalances;
    }
    if (Number.isFinite(Number(ptRaw))) {
      next.PTSessions = Number(ptRaw);
    }
    if (Number.isFinite(Number(gsRaw))) {
      next.GroupSessions = Number(gsRaw);
    }
    return next;
  });
}
